import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import React, { FC, ReactNode } from "react";
import { StaffTooltip as StaffTooltipQueryDoc } from "./queries.graphql";
import { StaffTooltipQuery, StaffTooltipQueryVariables } from "./queries.types";
import { StaffTooltipContent } from "./StaffTooltipContent";
import { useHoverCard } from "@sinch/components/application/HoverCard/useHoverCard";
import {
  ARROW_CLASS,
  TooltipArrow,
  TooltipPaper,
  TooltipPopper,
} from "@sinch/components/application/HoverCard/styledComponents";

export interface StaffTooltipProps {
  /**
   * Id of the staff member (worker) whose profile card is shown.
   */
  workerId: string;
  /**
   * Existing DOM element to anchor the card to (e.g. the web-component host that captured
   * the trigger markup). Takes precedence over `children`; when omitted, the first child
   * element is used as the anchor instead.
   */
  anchorElement?: HTMLElement;
  /**
   * Trigger rendered inline; the card anchors to the first child element. That child must
   * forward its `ref` to a DOM node. Ignored when `anchorElement` is provided.
   */
  children?: ReactNode;
}

/**
 * Renders the worker profile card (`StaffHeader`) inside an MUI Popper on hover. The card
 * anchors either to an explicit `anchorElement` (e.g. a web-component host) or, when that is
 * omitted, to the first child element passed in - so the same component works both as a
 * web-component view and as an inline wrapper around a trigger. Worker data is fetched lazily
 * the first time the tooltip opens and is then served from the Apollo cache on subsequent opens.
 */
export const StaffMemberTooltip: FC<StaffTooltipProps> = ({ workerId, anchorElement, children }) => {
  const { trigger, hasOpened, popperProps, paperProps, arrowRef } = useHoverCard({ anchorElement, children });

  const { data } = useQueryHandler<StaffTooltipQuery, StaffTooltipQueryVariables>(
    StaffTooltipQueryDoc,
    { id: workerId },
    { skip: !hasOpened }
  );

  return (
    <>
      {trigger}
      <TooltipPopper {...popperProps}>
        <TooltipPaper {...paperProps}>
          <TooltipArrow ref={arrowRef} className={ARROW_CLASS} />
          <StaffTooltipContent worker={data?.worker} />
        </TooltipPaper>
      </TooltipPopper>
    </>
  );
};
